import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getQuotes, getAccounts, createQuote, updateQuote, updateQuoteStatus, getOpportunities, getOpportunity, getUsers, exportQuotesExcel, bulkUpdateQuotes, getCompanySettings, optional } from '../api';
import Modal from '../components/Modal';
import SortableHeader from '../components/SortableHeader';
import ExportMenu from '../components/ExportMenu';
import { ColumnsButton } from '../components/ColumnConfigurator';
import MultiSelectFilter from '../components/MultiSelectFilter';
import useColumns from '../hooks/useColumns';
import usePagination from '../hooks/usePagination';
import Pagination from '../components/Pagination';
import { TableSkeleton, CardSkeleton } from '../components/TableSkeleton';
import RowActions from '../components/RowActions';
import EmptyValue from '../components/EmptyValue';
import MobileCard from '../components/MobileCard';
import useMediaQuery from '../hooks/useMediaQuery';
import { Plus, Search, FileText, Edit, ChevronDown, List, LayoutGrid, X, Eye } from 'lucide-react';
import QuotesKanban from '../components/QuotesKanban';
import { fmtCurrency } from '../utils/formatCurrency';
import EmptyState, { EmptyStateFiltered } from '../components/EmptyState';
import { useToast } from '../context/ToastContext';

function Quotes() {
  const { canDo } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const { visibleColumns } = useColumns('quotes');
  const [searchParams] = useSearchParams();
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Reference data
  const [accounts, setAccounts] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');

  // Filters
  const [statusFilter, setStatusFilter] = useState(() => {
    const param = searchParams.get('status');
    return param ? param.split(',') : [];
  });
  const [accountFilter, setAccountFilter] = useState(() => {
    const param = searchParams.get('account_id');
    return param ? param.split(',') : [];
  });

  // Selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedOpportunityId, setSelectedOpportunityId] = useState('');
  const [creating, setCreating] = useState(false);

  // View mode
  const [viewMode, setViewMode] = useState('table');

  // --- Server-side pagination for TABLE view ---
  const extraParams = useMemo(() => {
    const p = {};
    if (statusFilter.length) p.status = statusFilter.join(',');
    if (accountFilter.length) p.account_id = accountFilter.join(',');
    return p;
  }, [statusFilter, accountFilter]);

  const {
    data: tableData,
    loading: tableLoading,
    page,
    setPage,
    limit,
    setLimit,
    total,
    totalPages,
    search,
    setSearch,
    sortBy,
    sortOrder,
    handleSort,
    refetch,
  } = usePagination(getQuotes, {
    queryKey: 'quotes',
    initialLimit: 25,
    initialSortBy: 'quote_number',
    initialSortOrder: 'desc',
    extraParams,
  });

  // --- Kanban view gets unpaginated data ---
  const [kanbanData, setKanbanData] = useState([]);
  const [kanbanLoading, setKanbanLoading] = useState(false);

  const loadKanbanData = useCallback(async () => {
    setKanbanLoading(true);
    try {
      const res = await getQuotes({ search, status: statusFilter.join(',') || undefined, account_id: accountFilter.join(',') || undefined });
      const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setKanbanData(data);
    } finally {
      setKanbanLoading(false);
    }
  }, [search, statusFilter, accountFilter]);

  useEffect(() => {
    if (viewMode === 'kanban') loadKanbanData();
  }, [viewMode, loadKanbanData]);

  // Load reference data once
  useEffect(() => {
    loadReferenceData();
  }, []);

  const loadReferenceData = async () => {
    try {
      const [accountsRes, opportunitiesRes, usersRes] = await Promise.all([
        optional(getAccounts()),
        optional(getOpportunities()),
        getUsers(),
      ]);
      setAccounts(accountsRes.data);
      const oppData = Array.isArray(opportunitiesRes.data) ? opportunitiesRes.data : (opportunitiesRes.data?.data || []);
      setOpportunities(oppData);
      setUsers(usersRes.data);
    } catch (err) {
      console.error('Failed to load reference data:', err);
    }
  };

  const handleCreateFromOpportunity = async (e) => {
    e.preventDefault();
    if (!selectedOpportunityId) return;

    setCreating(true);
    try {
      const oppRes = await getOpportunity(selectedOpportunityId);
      const opp = oppRes.data;

      let defaultTerms = '';
      try {
        const settingsRes = await getCompanySettings();
        defaultTerms = settingsRes.data?.default_terms || 'All prices are valid for the period specified above. Payment terms are as stated. Shipping dates are estimates and subject to change.';
      } catch (e) {
        defaultTerms = 'All prices are valid for the period specified above. Payment terms are as stated. Shipping dates are estimates and subject to change.';
      }

      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + 30);

      const quoteData = {
        account_id: opp.account_id,
        opportunity_id: opp.id,
        contact_id: opp.contact_id,
        owner_id: opp.owner_id,
        rep_firm_id: opp.rep_firm_id,
        status: 'Draft',
        expiration_date: expirationDate.toISOString().split('T')[0],
        date_issued: new Date().toISOString().split('T')[0],
        validity_period: '30 Days',
        payment_terms: 'Net 30',
        delivery_terms: 'FOB Origin',
        currency: 'USD',
        tax_rate: 0,
        shipping_cost: 0,
        discount_type: opp.discount_type || '',
        discount_value: opp.discount_value || 0,
        standard_terms: defaultTerms,
        line_items: opp.line_items?.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          description: item.description,
          sku: item.sku || '',
          quantity: item.quantity,
          unit_price: item.unit_price,
          lead_time: item.lead_time || '',
        })) || [],
      };

      const response = await createQuote(quoteData);
      setShowCreateModal(false);
      setSelectedOpportunityId('');
      navigate(`/quotes/${response.data.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create quote');
    } finally {
      setCreating(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Draft': return 'gray';
      case 'Sent': return 'primary';
      case 'Accepted': return 'success';
      case 'Rejected': return 'danger';
      default: return 'gray';
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === tableData.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(tableData.map(q => q.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkUpdate = async (updates) => {
    try {
      await bulkUpdateQuotes(selectedIds, updates);
      setSelectedIds([]);
      setShowBulkActions(false);
      refetch();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update quotes');
    }
  };

  const handleKanbanStatusChange = async (quoteId, newStatus) => {
    try {
      await updateQuoteStatus(quoteId, newStatus);
      loadKanbanData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update quote status');
    }
  };

  // Render cell for both table and mobile card
  const renderCell = (col, quote) => {
    switch (col.key) {
      case 'quote_number':
        return <Link to={`/quotes/${quote.id}`}>{quote.quote_number}</Link>;
      case 'account_name':
        return quote.account_name ? <Link to={`/accounts/${quote.account_id}`}>{quote.account_name}</Link> : <EmptyValue />;
      case 'total':
        return `$${fmtCurrency(quote.total)}`;
      case 'status':
        return <span className={`badge badge-${getStatusColor(quote.status)}`}>{quote.status}</span>;
      case 'created_at':
        return quote.created_at ? new Date(quote.created_at).toLocaleDateString() : <EmptyValue />;
      case 'expiration_date':
        return quote.expiration_date ? new Date(quote.expiration_date).toLocaleDateString() : <EmptyValue />;
      case 'owner_name':
        return quote.owner_name ? quote.owner_name : <EmptyValue />;
      case 'rep_firm_name':
        return quote.rep_firm_name ? quote.rep_firm_name : <EmptyValue />;
      default:
        return quote[col.key] ? quote[col.key] : <EmptyValue />;
    }
  };

  // Build row actions for a quote
  const getRowActions = (quote) => [
    { key: 'view', label: 'View', icon: Eye, onClick: () => navigate(`/quotes/${quote.id}`) },
    { key: 'edit', label: 'Edit', icon: Edit, onClick: () => navigate(`/quotes/${quote.id}`) },
  ];

  return (
    <div className={`quotes-page${viewMode === 'kanban' ? ' kanban-active' : ''}`}>
      <div className="page-header">
        <h1 className="page-title">Quotes</h1>
        <div className="page-actions">
          <div className="view-toggle">
            <button
              className={`view-toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
            >
              <List size={16} />
              Table
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'kanban' ? 'active' : ''}`}
              onClick={() => setViewMode('kanban')}
            >
              <LayoutGrid size={16} />
              Kanban
            </button>
          </div>
          <ColumnsButton entityType="quotes" />
          <ExportMenu
            onExportExcel={exportQuotesExcel}
            entityName="Quotes"
          />
          {canDo('quotes', 'create') && (
            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
              <Plus size={18} /> New Quote
            </button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error" role="alert" aria-live="assertive">{error}</div>}

      <div className="card mb-4">
        <div className="card-body">
          <div className="filter-bar">
            <div className="search-input">
              <Search size={18} className="search-input-icon" aria-hidden="true" />
              <input type="text" className="form-input" placeholder="Search quotes..." value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search quotes" />
            </div>
            <MultiSelectFilter
              label="Status"
              options={[{ id: 'Draft', name: 'Draft' }, { id: 'Sent', name: 'Sent' }, { id: 'Accepted', name: 'Accepted' }, { id: 'Rejected', name: 'Rejected' }]}
              selected={statusFilter}
              onChange={setStatusFilter}
              className="filter-dropdown-sm"
              ariaLabel="Filter by status"
            />
            <MultiSelectFilter
              label="Accounts"
              options={accounts}
              selected={accountFilter}
              onChange={setAccountFilter}
              className="filter-dropdown-md"
              ariaLabel="Filter by account"
            />
            {(search || statusFilter.length > 0 || accountFilter.length > 0) && (
              <button
                className="btn-clear-filters"
                onClick={() => { setSearch(''); setStatusFilter([]); setAccountFilter([]); }}
              >
                <X size={14} />
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedIds.length > 0 && viewMode === 'table' && (
        <div className="bulk-actions-bar">
          <span>{selectedIds.length} selected</span>
          <div className="bulk-actions">
            {canDo('quotes', 'edit') && (
              <div className="dropdown">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowBulkActions(showBulkActions === 'owner' ? false : 'owner')}
                >
                  Change Owner
                  <ChevronDown size={16} />
                </button>
                {showBulkActions === 'owner' && (
                  <div className="dropdown-menu">
                    {users.map(u => (
                      <button
                        key={u.id}
                        className="dropdown-item"
                        onClick={() => handleBulkUpdate({ owner_id: u.id })}
                      >
                        {u.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Kanban View */}
      {viewMode === 'kanban' ? (
        kanbanLoading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <QuotesKanban quotes={kanbanData} onStatusChange={handleKanbanStatusChange} canEdit={canDo('quotes', 'edit')} />
        )
      ) : isMobile ? (
        /* Mobile Card View */
        <div>
          {tableLoading ? (
            <CardSkeleton count={4} />
          ) : tableData.length === 0 ? (
            <div className="card">
              <div className="card-body">
                {search || statusFilter.length > 0 || accountFilter.length > 0 ? (
                  <EmptyStateFiltered
                    onClearFilters={() => { setSearch(''); setStatusFilter([]); setAccountFilter([]); }}
                  />
                ) : (
                  <EmptyState
                    icon={FileText}
                    title="No quotes yet"
                    description="Create your first quote from an opportunity to start generating proposals."
                    actionLabel={canDo('quotes', 'create') ? 'New Quote' : undefined}
                    onAction={canDo('quotes', 'create') ? () => setShowCreateModal(true) : undefined}
                    actionIcon={Plus}
                  />
                )}
              </div>
            </div>
          ) : (
            <>
              {tableData.map(quote => (
                <MobileCard
                  key={quote.id}
                  columns={visibleColumns}
                  data={quote}
                  renderCell={(col, data) => renderCell(col, data)}
                  selected={selectedIds.includes(quote.id)}
                  onSelect={() => toggleSelect(quote.id)}
                  actions={<RowActions actions={getRowActions(quote)} id={quote.id} />}
                  primaryField="quote_number"
                />
              ))}
              <Pagination
                page={page}
                totalPages={totalPages}
                total={total}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={setLimit}
              />
            </>
          )}
        </div>
      ) : (
        /* Desktop Table View */
        <div className="card">
          <div className="table-container">
            <table className="table" aria-label="Quotes list">
              <thead>
                <tr>
                  <th scope="col" style={{ width: 40 }}>
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={selectedIds.length === tableData.length && tableData.length > 0}
                      onChange={toggleSelectAll}
                      aria-label="Select all quotes"
                    />
                  </th>
                  {visibleColumns.map(col => (
                    <SortableHeader key={col.key} label={col.label} field={col.key} sortField={sortBy} sortDirection={sortOrder} onSort={handleSort} />
                  ))}
                  <th scope="col" className="col-actions"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {tableLoading ? (
                  <TableSkeleton columns={visibleColumns.length} rows={8} hasCheckbox hasActions />
                ) : tableData.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumns.length + 2}>
                      {search || statusFilter.length > 0 || accountFilter.length > 0 ? (
                        <EmptyStateFiltered
                          onClearFilters={() => { setSearch(''); setStatusFilter([]); setAccountFilter([]); }}
                        />
                      ) : (
                        <EmptyState
                          icon={FileText}
                          title="No quotes yet"
                          description="Create your first quote from an opportunity to start generating proposals."
                          actionLabel={canDo('quotes', 'create') ? 'New Quote' : undefined}
                          onAction={canDo('quotes', 'create') ? () => setShowCreateModal(true) : undefined}
                          actionIcon={Plus}
                        />
                      )}
                    </td>
                  </tr>
                ) : (
                  tableData.map(quote => (
                    <tr key={quote.id}>
                      <td>
                        <input type="checkbox" className="checkbox" checked={selectedIds.includes(quote.id)} onChange={() => toggleSelect(quote.id)} />
                      </td>
                      {visibleColumns.map(col => (
                        <td key={col.key}>{renderCell(col, quote)}</td>
                      ))}
                      <td>
                        <RowActions actions={getRowActions(quote)} id={quote.id} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {!tableLoading && tableData.length > 0 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              limit={limit}
              onPageChange={setPage}
              onLimitChange={setLimit}
            />
          )}
        </div>
      )}

      {/* Create Quote Modal */}
      <Modal isOpen={showCreateModal} onClose={() => { setShowCreateModal(false); setSelectedOpportunityId(''); }} title="New Quote">
        <form onSubmit={handleCreateFromOpportunity}>
          <div className="modal-body">
            <p className="text-secondary" style={{ marginBottom: '16px' }}>
              Select an opportunity to create a quote from. Account, contact, owner, products, and discount will be copied automatically.
            </p>
            <div className="form-group">
              <label className="form-label">Opportunity *</label>
              <select
                className="form-select"
                value={selectedOpportunityId}
                onChange={(e) => setSelectedOpportunityId(e.target.value)}
                required
              >
                <option value="">Select Opportunity</option>
                {opportunities.map(o => (
                  <option key={o.id} value={o.id}>
                    {o.name} — {o.account_name} (${fmtCurrency(o.calculated_amount || o.amount || 0)})
                  </option>
                ))}
              </select>
            </div>
            {selectedOpportunityId && (() => {
              const opp = opportunities.find(o => o.id === parseInt(selectedOpportunityId));
              if (!opp) return null;
              return (
                <div style={{ background: 'var(--bg-tertiary)', padding: '12px 16px', borderRadius: 'var(--radius-md)', fontSize: '13px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span className="text-secondary">Account</span>
                    <span style={{ fontWeight: 500 }}>{opp.account_name}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span className="text-secondary">Stage</span>
                    <span style={{ fontWeight: 500 }}>{opp.stage_name || '-'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span className="text-secondary">Owner</span>
                    <span style={{ fontWeight: 500 }}>{opp.owner_name || '-'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span className="text-secondary">Products</span>
                    <span style={{ fontWeight: 500 }}>{opp.line_items_count || 0} items</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--border-color)', paddingTop: '8px', marginTop: '4px' }}>
                    <span className="text-secondary">Amount</span>
                    <span style={{ fontWeight: 600, color: 'var(--success)' }}>${fmtCurrency(opp.calculated_amount || opp.amount || 0)}</span>
                  </div>
                </div>
              );
            })()}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => { setShowCreateModal(false); setSelectedOpportunityId(''); }}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={creating}>
              {creating ? 'Creating...' : 'Create Quote'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default Quotes;
